import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';

export interface CreditHistoryDataPoint {
  reportDate: number;
  score: number;
}

@Component({
    selector: 'bw-credit-history-line-chart-legacy',
    imports: [CommonModule, HighchartsChartModule],
    templateUrl: './credit-history-line-chart.component.html',
    styleUrls: ['./credit-history-line-chart.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreditHistoryLineChartComponentLegacy implements OnChanges {
  @Input() data: CreditHistoryDataPoint[] = [];

  /**
   * BCP 47 locale for x-axis month labels (e.g. 'en-US', 'es', 'fr').
   * Defaults to 'en-US' when not set. Pass the user's preferred language from LanguageSelectorService.activeLang.
   */
  @Input() locale = 'en-US';

  /**
   * When this value changes (e.g. after window resize), the chart is destroyed and re-rendered
   * so it sizes to the current container (reflow alone may not work if the container has fixed width).
   */
  @Input() resizeTrigger: number;

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = this._buildChartOptions([]);

  /** Toggled off then on when resizeTrigger changes to force chart destroy + re-render. */
  _showChart = true;

  private readonly _cdr = inject(ChangeDetectorRef);
  private _chart: Highcharts.Chart | null = null;

  onChartInstance(chart: Highcharts.Chart): void {
    this._chart = chart;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['data'] || changes['locale']) && this.data?.length) {
      this.chartOptions = this._buildChartOptions(this.data);
    }
    const resizeChange = changes['resizeTrigger'];
    if (
      resizeChange &&
      !resizeChange.firstChange &&
      this.data?.length
    ) {
      this._chart = null;
      this._showChart = false;
      this._cdr.markForCheck();
      setTimeout(() => {
        this._showChart = true;
        this._cdr.markForCheck();
      }, 0);
    }
  }

  private _getMonthsBetween(start: Date, end: Date): Date[] {
    const months: Date[] = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (current <= endMonth) {
      months.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }
    return months;
  }

  private _buildChartOptions(data: CreditHistoryDataPoint[]): Highcharts.Options {
    const sorted = [...data].sort((a, b) => a.reportDate - b.reportDate);
    const lastSix = sorted.slice(-6);
    if (lastSix.length === 0) {
      return {
        chart: { type: 'line', backgroundColor: 'transparent' },
        title: { text: undefined },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: { categories: [] },
        yAxis: { min: 300, max: 900 },
        series: [{ type: 'line', data: [] }],
      };
    }
    const locale = this.locale || 'en-US';
    const minDate = new Date(lastSix[0].reportDate);
    const maxDate = new Date(lastSix[lastSix.length - 1].reportDate);
    const allMonths = this._getMonthsBetween(minDate, maxDate);
    const categories = allMonths.map((d) =>
      d.toLocaleDateString(locale, { month: 'short' })
    );
    const scoreByMonth = new Map<string, number>();
    for (const d of lastSix) {
      const dt = new Date(d.reportDate);
      const key = `${dt.getFullYear()}-${dt.getMonth()}`;
      scoreByMonth.set(key, d.score);
    }
    const seriesData: Array<{ x: number; y: number }> = [];
    allMonths.forEach((d, index) => {
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (scoreByMonth.has(key)) {
        seriesData.push({ x: index, y: scoreByMonth.get(key)! });
      }
    });
    const scoresOnly = lastSix.map((d) => d.score);
    const dataMin = Math.min(...scoresOnly);
    const dataMax = Math.max(...scoresOnly);
    const padding = 50;
    const yMin = Math.max(300, dataMin - padding);
    const yMax = Math.min(900, dataMax + padding);

    return {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        spacingBottom: 20,
        spacingTop: 10,
        style: {
          fontFamily: 'inherit',
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      tooltip: {
        formatter: function () {
          return this.y != null ? String(this.y) : '';
        },
      },
      xAxis: {
        categories,
        lineColor: '#e0e0e0',
        tickColor: '#e0e0e0',
        labels: {
          style: {
            color: 'var(--secondary-text, #666)',
            fontSize: '12px',
          },
        },
      },
      yAxis: {
        title: { text: undefined },
        min: yMin,
        max: yMax,
        tickInterval: 50,
        gridLineColor: '#e8e8e8',
        lineColor: '#e0e0e0',
        tickColor: '#e0e0e0',
        labels: {
          style: {
            color: 'var(--secondary-text, #666)',
            fontSize: '12px',
          },
        },
      },
      plotOptions: {
        line: {
          marker: {
            radius: 4,
            symbol: 'circle',
          },
          lineWidth: 2,
        },
      },
      series: [
        {
          type: 'line',
          data: seriesData,
          color: 'var(--blue-50, #1976d2)',
          marker: {
            fillColor: 'var(--blue-50, #1976d2)',
            lineWidth: 0,
          },
        },
      ],
    };
  }
}
